import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  postRefine,
  REFINE_TONE_LABELS,
  type RefineTone,
} from "@/lib/emails";
import { cn } from "@/lib/cn";

interface RefineOverlayProps {
  emailId: string;
  initialBody: string;
  onClose: () => void;
  /** Fires with the final body after the user presses Apply & return.
   *  The cockpit propagates this into its FocusPane draft state. */
  onApply: (body: string) => void;
}

const TONES: RefineTone[] = [
  "more_formal",
  "shorter",
  "firmer",
  "warmer",
  "add_deadline",
  "fix_grammar",
];

/** Refine-tone overlay (brief §9, reference image 3).
 *
 *  Modal layout:
 *  - Left: current draft body (read-only after the first refine to
 *    keep the diff meaningful; the user always returns to the
 *    cockpit to edit).
 *  - Right: tone pills + Live before/after diff.
 *  - Footer: Cancel / Apply & return.
 *
 *  The "Run" button fires POST /api/emails/{id}/refine. Multiple
 *  runs are allowed — every run replaces the right-hand body and
 *  records its tone set in the run-history strip.
 */
export function RefineOverlay({
  emailId,
  initialBody,
  onClose,
  onApply,
}: RefineOverlayProps) {
  const [selectedTones, setSelectedTones] = useState<Set<RefineTone>>(
    new Set(),
  );
  const [refinedBody, setRefinedBody] = useState<string | null>(null);
  const [changesSummary, setChangesSummary] = useState<string>("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runHistory, setRunHistory] = useState<
    Array<{ tones: RefineTone[]; summary: string }>
  >([]);

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleTogglePill = (tone: RefineTone) => {
    setSelectedTones((prev) => {
      const next = new Set(prev);
      if (next.has(tone)) next.delete(tone);
      else next.add(tone);
      return next;
    });
  };

  const handleRun = async () => {
    if (selectedTones.size === 0) {
      setError("Pick at least one tone.");
      return;
    }
    setError(null);
    setRunning(true);
    const orderedTones = TONES.filter((t) => selectedTones.has(t));
    try {
      const response = await postRefine(emailId, orderedTones);
      setRefinedBody(response.after);
      setChangesSummary(response.changes_summary);
      setRunHistory((h) => [
        ...h,
        { tones: orderedTones, summary: response.changes_summary },
      ]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setRunning(false);
    }
  };

  const handleApply = () => {
    if (refinedBody) onApply(refinedBody);
  };

  const displayedAfter = refinedBody ?? "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Refine reply"
      data-testid="refine-overlay"
      className="fixed inset-0 z-50 flex items-stretch bg-black/60 p-2 md:p-8"
    >
      <div className="m-auto flex h-full w-full max-w-5xl flex-col rounded-lg border border-op-border bg-op-surface shadow-2xl md:h-[90vh]">
        <header className="flex items-center justify-between border-b border-op-border px-4 py-3">
          <h2 className="text-sm font-medium text-op-primary">Refine reply</h2>
          <Button
            data-testid="refine-overlay-close"
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden px-4 py-3 md:grid-cols-2">
          <section className="flex min-h-0 flex-col">
            <h3 className="mb-2 text-[10px] uppercase tracking-wider text-op-muted">
              Current draft
            </h3>
            <pre
              data-testid="refine-overlay-before"
              className="min-h-[180px] flex-1 overflow-auto whitespace-pre-wrap break-words rounded border border-op-border bg-op-base px-3 py-3 font-sans text-sm text-op-secondary"
            >
              {initialBody}
            </pre>
          </section>

          <section className="flex min-h-0 flex-col">
            <h3 className="mb-2 text-[10px] uppercase tracking-wider text-op-muted">
              Tone changes
            </h3>
            <ul className="mb-3 flex flex-wrap gap-1.5">
              {TONES.map((tone) => {
                const active = selectedTones.has(tone);
                return (
                  <li key={tone}>
                    <button
                      type="button"
                      data-testid={`refine-pill-${tone}`}
                      onClick={() => handleTogglePill(tone)}
                      aria-pressed={active}
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs transition-colors",
                        active
                          ? "border-op-accent bg-op-accent-muted text-op-primary"
                          : "border-op-border text-op-secondary hover:bg-op-elevated",
                      )}
                    >
                      {REFINE_TONE_LABELS[tone]}
                    </button>
                  </li>
                );
              })}
            </ul>
            <Button
              data-testid="refine-overlay-run"
              variant="default"
              className="mb-3 bg-op-accent text-op-primary hover:bg-op-accent/90"
              disabled={running || selectedTones.size === 0}
              onClick={handleRun}
            >
              {running ? "Refining…" : "Run refine"}
            </Button>
            {error ? (
              <p
                role="alert"
                data-testid="refine-overlay-error"
                className="mb-3 rounded border border-op-border bg-op-elevated px-2 py-1 text-xs text-op-secondary"
              >
                {error}
              </p>
            ) : null}
            <pre
              data-testid="refine-overlay-after"
              className="min-h-[180px] flex-1 overflow-auto whitespace-pre-wrap break-words rounded border border-op-border bg-op-base px-3 py-3 font-sans text-sm text-op-primary"
            >
              {displayedAfter || "Pick one or more tones above and press Run refine to see the rewritten body."}
            </pre>
            {changesSummary ? (
              <p
                data-testid="refine-overlay-summary"
                className="mt-2 text-[11px] text-op-secondary"
              >
                {changesSummary}
              </p>
            ) : null}
            {runHistory.length > 1 ? (
              <details
                data-testid="refine-overlay-history"
                className="mt-2 text-[11px] text-op-muted"
              >
                <summary className="cursor-pointer">
                  {runHistory.length} refine runs
                </summary>
                <ul className="mt-1 space-y-1">
                  {runHistory.map((run, i) => (
                    <li key={i}>
                      <span className="font-mono">{run.tones.join(", ")}</span>
                      <span className="ml-2 text-op-secondary">
                        {run.summary}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-op-border px-4 py-3">
          <Button
            data-testid="refine-overlay-cancel"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            data-testid="refine-overlay-apply"
            variant="default"
            className="bg-op-accent text-op-primary hover:bg-op-accent/90"
            disabled={!refinedBody}
            onClick={handleApply}
          >
            Apply &amp; return
          </Button>
        </footer>
      </div>
    </div>
  );
}
